import { t } from 'i18next';
import { findKey, identity, intersection, xor } from 'lodash';
import { useState, useEffect, useCallback } from 'react';

import { compressorTypes, DeviceType } from '@/constants/deviceTypeEnum';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { DeviceKind } from '@/constants/enums';
import { mapToOptions } from '@/comp/MapBuilder/components/PIDEditing/PIDEditing.utils';

import EditCompressorForm from './CompressorDetailsTab/EditCompressorForm/EditCompressorForm';
import { Entity } from '../NodeWidget.types';
import EditTankForm from './CompressorDetailsTab/EditTankForm/EditTankForm';
import EditDryerForm from './CompressorDetailsTab/EditDryerForm/EditDryerForm';
import EditProductionLineForm from './CompressorDetailsTab/EditProductionLineForm/EditProductionLineForm';
import EditBoosterForm from './CompressorDetailsTab/EditBoosterForm/EditBoosterForm';
import CompressorDetailsTab from './CompressorDetailsTab/CompressorDetailsTab';
import OnDeviceSensorTab from './OnDeviceSensorTab/OnDeviceSensorTab';
import { StationTier } from '@/constants/enums';
import { PhysicalSchema } from '@/store/Station/constants';

export const ASSET_DEVICE_TYPES = [
  ...compressorTypes,
  DeviceType.DryAirTank,
  DeviceType.WetAirTank,
  DeviceType.DesiccantAirDryer,
  DeviceType.RefrigeratedAirDryer,
  DeviceType.Booster,
  DeviceType.ProductionFloor,
];

export enum DEFAULT_MODEL_IDS {
  productionFloor = '7a363818-5321-413a-b625-b17f550a90e6',
  dryAirTank = '64f919e9-da0d-4529-945a-3917b563b95a',
  wetAirTank = '024fc8f5-f7d0-45e1-9012-11c9cc6f85d5',
  refrigeratedAirDryer = '0e47e864-5178-45d9-8322-39b45e75f961',
  dummyCompressor = 'fb5c0c77-8323-4d60-9b21-cabb0f1ba9c2',
  desiccantAirDryer = 'cb490cc2-c75b-41c9-8cc4-b293fb9191d6',
}

export enum DUMMY_MODEL_IDS {
  dryAirTank = '0e928ba7-8049-4fa0-99b1-6fdcdbef1832',
  wetAirTank = 'b952d56f-125a-4bc3-9457-e1751e312945',
  refrigeratedAirDryer = 'd026741f-2cef-47e8-bba5-3374eb6e0226',
  dummyCompressor = 'e7d52a5d-cf1b-43ad-bbea-6ee346f1f96c',
  desiccantAirDryer = '905d850e-f8b6-416d-91b0-d981f54ad233',
  booster = '35d87bc114bf3b989bfeb',
  powerSensor = '3cc84b53-ee88-4c2a-ae1b-bb9128649ded',
  flowSensor = 'a718aa38-d848-4788-a628-7f2709e62bc6',
  pressureSensor = '130103f0-fd44-48f1-ab16-0a2f088dd3b6',
  airTemperatureSensor = '6830d1fd-345b-4019-b220-adb665b881e1',
  dewpointSensor = '97890241-ec3f-43a6-b4a2-47697dde0847',
  spillValve = '1605d945-52f1-4b2a-82ea-41f7c1e07b54',
  flowSensor_pressureSensor = 'd67ba79c-cce5-48cf-9a87-2af53ca699ac',
  dewpointSensor_pressureSensor = '43762ce6-3c61-484b-9c66-3e2db8a61df6',
}

const ALL_DUMMY_MODEL_IDS: string[] = [...Object.values(DEFAULT_MODEL_IDS), ...Object.values(DUMMY_MODEL_IDS)];

interface DeviceModelOption {
  value: string;
  label: string;
  deviceKind?: DeviceKind;
  controllerId?: string;
}

export const deviceSuperTypeMapping = {
  compressor: { types: compressorTypes, component: EditCompressorForm },
  tank: { types: [DeviceType.DryAirTank, DeviceType.WetAirTank], component: EditTankForm },
  dryer: { types: [DeviceType.DesiccantAirDryer, DeviceType.RefrigeratedAirDryer], component: EditDryerForm },
  booster: { types: [DeviceType.Booster], component: EditBoosterForm },
  'production line': { types: [DeviceType.ProductionFloor], component: EditProductionLineForm },
};

export const getDeviceSuperType = (deviceType: DeviceType) =>
  findKey(deviceSuperTypeMapping, value => value.types.includes(deviceType));

export const useDeviceModels = (
  entity: Entity,
  afterOpenChange: boolean,
  t,
  currentDeviceType?: DeviceType,
  initialDeviceModelId?: string,
) => {
  const { pidEditStore } = useStores();
  const [deviceModelOptions, setDeviceModelOptions] = useState<DeviceModelOption[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(true);
  const [filteredModelOptions, setFilteredModelOptions] = useState(deviceModelOptions);

  useEffect(() => {
    const getDeviceModelOptions = async () => {
      const deviceType = currentDeviceType || entity.deviceType;

      // Clear existing options immediately when device type changes
      setDeviceModelOptions([]);
      setIsLoadingModels(true);

      try {
        const sanitizedDeviceType = deviceType.replace(/_/g, ',');
        const options = await pidEditStore.getModelSpec(sanitizedDeviceType);

        //Backward compatibility for existing devices only
        const isExistingDevice = !!entity.deviceId;

        if (
          isExistingDevice &&
          initialDeviceModelId &&
          !options.find(option => option.value === initialDeviceModelId)
        ) {
          const initialDeviceModel = await pidEditStore.getDeviceModel(initialDeviceModelId);
          options.push(initialDeviceModel);
        }

        setDeviceModelOptions(options || []);
      } catch (error) {
        console.error('Error loading device models:', error);
        setDeviceModelOptions([]);
      } finally {
        setIsLoadingModels(false);
      }
    };

    if (afterOpenChange) {
      getDeviceModelOptions();
    }
  }, [afterOpenChange, currentDeviceType, entity.deviceType, pidEditStore]);

  useEffect(() => {
    setFilteredModelOptions(deviceModelOptions);
  }, [deviceModelOptions]);

  const onModelSearch = useCallback(
    value => {
      const filteredOptions = deviceModelOptions.filter(option =>
        value ? option.label.toLowerCase().includes(value.toLowerCase()) : true,
      );
      const deviceTypeForDefault = currentDeviceType || entity.deviceType;
      const dummyModelId = DUMMY_MODEL_IDS[deviceTypeForDefault] ?? DUMMY_MODEL_IDS.dummyCompressor;
      if (!filteredOptions?.length) {
        setFilteredModelOptions([{ value: dummyModelId, label: t('No matching Spec') }]);
      } else {
        setFilteredModelOptions(filteredOptions);
      }
    },
    [deviceModelOptions, currentDeviceType, entity.deviceType, t],
  );

  return { deviceModelOptions: filteredModelOptions, isLoadingModels, onModelSearch };
};

export const areValuesValid = (values, currentDeviceType = null) => {
  const deviceType = currentDeviceType || values.deviceType;

  if (compressorTypes.includes(deviceType)) return !!values.connectTo?.length;
  if (deviceType === DeviceType.ProductionFloor) return !!values.connectFrom?.length;
  return !!values.connectTo?.length && !!values.connectFrom?.length;
};

export const orphanAssetsExist = (deviceId, values, draftDevicesWithoutPipes) => {
  const updatedDevices = getUpdatedDevices(deviceId, values, draftDevicesWithoutPipes);
  const orphanDevices: string[] = [];

  updatedDevices.forEach(device => {
    const currentDeviceId = device.deviceId;
    if (!currentDeviceId) return;

    if (intersection(device.deviceTypes, compressorTypes).length) return;

    const appearsInOnDevices = draftDevicesWithoutPipes.some(otherDevice => {
      const onDevices = otherDevice.deviceDto?.physicalSchema?.onDevices;
      return onDevices && Array.isArray(onDevices) && onDevices.includes(currentDeviceId);
    });
    if (appearsInOnDevices) return;

    const toDevices = device.toDevices;
    const hasNoToConnections = !toDevices || !Array.isArray(toDevices) || toDevices.length === 0;

    const hasNoFromConnections = !updatedDevices.some(otherDevice => {
      if (otherDevice.deviceId === currentDeviceId) return false;
      const otherToDevices = otherDevice.toDevices;
      return otherToDevices && Array.isArray(otherToDevices) && otherToDevices.includes(currentDeviceId);
    });

    if (hasNoToConnections && hasNoFromConnections) {
      const deviceName =
        draftDevicesWithoutPipes.find(d => d.deviceDto?.id === currentDeviceId)?.deviceDto?.name || currentDeviceId;
      orphanDevices.push(deviceName);
    }
  });

  return orphanDevices;
};

export const getUpdatedDevices = (deviceId, values, draftDevicesWithoutPipes) => {
  const { connectTo, connectFrom } = values;

  if (!deviceId) {
    return draftDevicesWithoutPipes;
  }

  const updatedDevices = draftDevicesWithoutPipes.map(device => {
    const currentDeviceId = device.deviceDto?.id;
    let currentToDevices = device.deviceDto?.physicalSchema?.toDevices || [];

    if (currentDeviceId === deviceId) {
      currentToDevices = Array.isArray(connectTo) ? [...connectTo] : [];
    } else {
      const hasDeviceIdInToDevices = currentToDevices.includes(deviceId);
      const shouldHaveDeviceId = Array.isArray(connectFrom) && connectFrom.includes(currentDeviceId);

      if (shouldHaveDeviceId && !hasDeviceIdInToDevices) {
        currentToDevices.push(deviceId);
      } else if (!shouldHaveDeviceId && hasDeviceIdInToDevices) {
        currentToDevices = currentToDevices.filter(id => id !== deviceId);
      }
    }

    return { deviceId: currentDeviceId, toDevices: currentToDevices };
  });

  return updatedDevices;
};

export const useFormHelper = (
  t,
  entity: Entity,
  afterOpenChange: boolean,
  messageApi,
  mapFormValuesToPayload: (
    values: Record<string, unknown>,
    entity: Entity,
    initialValues: Record<string, unknown>,
    tvalue?: (value: number, unit: string) => number,
  ) => Record<string, unknown> = identity,
  useInitialValues: (entity: Entity) => Record<string, unknown> = identity,
  currentDeviceType?: DeviceType,
) => {
  const { pidEditStore, stationStore } = useStores();
  const initialValues = useInitialValues(entity);
  const { deviceModelOptions, isLoadingModels, onModelSearch } = useDeviceModels(
    entity,
    afterOpenChange,
    t,
    currentDeviceType,
    initialValues.deviceModelId as string,
  );
  const { tvalue } = useMeasurement();

  const deviceIdsInOnDevices = new Set<string>();
  stationStore.currentStation.devices.forEach(device => {
    if (Array.isArray(device.onDevices)) {
      device.onDevices.forEach(deviceId => deviceIdsInOnDevices.add(deviceId));
    }
    if (Array.isArray(device.physicalSchema?.onDevices)) {
      device.physicalSchema.onDevices.forEach(deviceId => deviceIdsInOnDevices.add(deviceId));
    }
  });

  const deviceConnectionOptions = mapToOptions(
    stationStore.currentStation.devices.filter(
      device =>
        device.deviceType !== DeviceType.Pipe &&
        device.deviceId !== entity.deviceId &&
        !deviceIdsInOnDevices.has(device.deviceId),
    ),
    'deviceId',
  );

  const onFinish = async values => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { deviceType, ...payloadValues } = values;
    try {
      if (!areValuesValid(values, currentDeviceType)) return messageApi.error(t('assetsMustHaveConnectionsUpdated'));
      const orphanNames = orphanAssetsExist(entity?.deviceId, values, pidEditStore.draftDevicesWithoutPipes);
      if (orphanNames.length > 0) {
        const orphanNamesList = orphanNames.map(deviceName => t('deviceNotConnected', { deviceName })).join('\n');
        return messageApi.error(orphanNamesList);
      }

      if (!entity?.deviceId) {
        await pidEditStore.createDeviceWithConnections(mapFormValuesToPayload(values, entity, initialValues, tvalue));
        pidEditStore.hideMenu();
        return;
      }
      if (
        !compressorTypes.includes(currentDeviceType) &&
        !areArraysEqual(initialValues.connectFrom, values.connectFrom)
      ) {
        await pidEditStore.updateConnectedDevices(entity, { connectFrom: values.connectFrom });
      }
      await pidEditStore.updateDevice(
        entity.deviceId,
        mapFormValuesToPayload(payloadValues, entity, initialValues, tvalue),
      );
      pidEditStore.hideMenu();
    } catch (error) {
      if (Array.isArray(error.message?.errors)) {
        messageApi.error(error.message?.errors?.join('\n'));
      } else if (typeof error?.errors === 'object') {
        messageApi.error(Object.values(error?.errors).join(', '));
      } else {
        messageApi.error(error.message);
      }
    }
  };

  return { deviceModelOptions, isLoadingModels, onFinish, onModelSearch, deviceConnectionOptions, initialValues };
};

export const getCommonSelectProps = (isMobile: boolean) => ({
  shouldRenderDrawer: isMobile,
  showSearch: true,
  optionFilterProp: 'children',
});

export const getFieldSelectProps = (isMobile: boolean, form, title: string, fieldName: string) => ({
  ...getCommonSelectProps(isMobile),
  drawerTitle: title,
  selectedValue: form.getFieldValue(fieldName),
});

export const IsDeprecatedModel = (deviceKind: DeviceKind) => {
  return deviceKind === DeviceKind.NoController || deviceKind === DeviceKind.Proxy;
};

/**
 * Checks if a device kind represents a disconnected device (no controller access).
 * Use this for determining if controller selector should be disabled.
 * @param deviceKind The device kind to check
 * @returns true if the device is disconnected or has no controller
 */
export const isDeviceKindDisconnected = (deviceKind: DeviceKind): boolean => {
  return deviceKind === DeviceKind.Disconnected || deviceKind === DeviceKind.NoController;
};

export const IsDisconnectedDevice = (deviceModelOptions: DeviceModelOption[], currentModelId: string) => {
  if (ALL_DUMMY_MODEL_IDS.includes(currentModelId)) {
    return true;
  }
  const selectedModel = deviceModelOptions?.find(model => model.value === currentModelId);
  return isDisconnectedModel(selectedModel);
};

export const isDisconnectedModel = (selectedModel: DeviceModelOption) => {
  return isDeviceKindDisconnected(selectedModel?.deviceKind);
};

export const getDeprecatedModelToolTip = () => {
  return t('NoControllerProxyNotEditable');
};

export const areArraysEqual = (initialConnectTo, currentConnectTo) =>
  initialConnectTo.length === currentConnectTo.length && xor(initialConnectTo, currentConnectTo).length === 0;

export function buildPhysicalSchema(entity: Entity, overrides?: Partial<PhysicalSchema>): PhysicalSchema {
  return {
    toDevices: overrides?.toDevices ?? [],
    relatedDevices: overrides?.relatedDevices ?? entity.relatedDevices ?? [],
    onDevices: overrides?.onDevices ?? entity.onDevices ?? [],
    elementIndex: overrides?.elementIndex ?? entity.elementIndex,
    primaryFlowSensor: overrides?.primaryFlowSensor ?? entity.primaryFlowSensor,
    primaryPressureSensor: overrides?.primaryPressureSensor ?? entity.primaryPressureSensor,
    isIndependent: overrides?.isIndependent ?? entity.isIndependent,
  };
}

export const getTabItems = (entity, t, afterOpenChange, setHasUnsavedChanges, stationTier: StationTier) => [
  {
    label: t(`${getDeviceSuperType(entity.deviceType)}Details`),
    key: 'compressorDetails',
    children: <CompressorDetailsTab entity={entity} afterOpenChange={afterOpenChange} />,
  },
  ...(stationTier === StationTier.Optimize
    ? [
        {
          label: t('onDeviceSensor'),
          key: 'onDeviceSensor',
          children: <OnDeviceSensorTab entity={entity} setHasUnsavedChanges={setHasUnsavedChanges} />,
        },
      ]
    : []),
];
